import { useState, useEffect, useRef } from "preact/hooks";
import { JSX } from "preact/jsx-runtime";
import { Icon } from "../../../components";
import css from "./NumberInput.module.scss";

export interface NumberInputProps {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  className?: string;
  style?: JSX.CSSProperties;
}

export const NumberInput = ({
  value = 1,
  min = 0,
  max = 100,
  step = 0.1,
  onChange,
  disabled,
}: NumberInputProps) => {
  const formatMinOneDecimal = (n: number): string => {
    const rounded = Math.round(n * 100) / 100;
    const two = rounded.toFixed(2);
    if (two.endsWith("00")) return rounded.toFixed(1);
    if (two.endsWith("0")) return two.slice(0, -1);
    return two;
  };

  const [displayValue, setDisplayValue] = useState<string | number>(
    formatMinOneDecimal(value)
  );
  const [lastValidValue, setLastValidValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatMinOneDecimal(value));
      setLastValidValue(value);
    }
  }, [value, isFocused]);

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;
    let v = rawValue.replace(/,/g, ".");
    v = v.replace(/[^0-9.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
      if (v.length > firstDot + 1 + 2) v = v.slice(0, firstDot + 1 + 2);
    }
    setDisplayValue(v);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (event: Event) => {
    setIsFocused(false);
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;
    const normalized = rawValue.replace(/,/g, ".");
    const numValue = parseFloat(normalized);

    let finalValue: number;

    if (isNaN(numValue) || normalized === "") {
      finalValue = Math.max(lastValidValue, min);
    } else if (numValue < min) {
      finalValue = min;
    } else if (numValue > max) {
      finalValue = max;
    } else {
      // Round to 2 decimals for internal value
      finalValue = Math.round(numValue * 100) / 100;
    }

    setDisplayValue(formatMinOneDecimal(finalValue));
    setLastValidValue(finalValue);
    onChange?.(finalValue);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const handleDecrease = () => {
    const currentValue =
      typeof displayValue === "string"
        ? parseFloat(displayValue)
        : displayValue;
    const validCurrentValue = isNaN(currentValue)
      ? lastValidValue
      : currentValue;
    const newValue = Math.max(min, validCurrentValue - step);
    const roundedValue = Math.round(newValue * 100) / 100;

    setDisplayValue(formatMinOneDecimal(roundedValue));
    setLastValidValue(roundedValue);
    onChange?.(roundedValue);
  };

  const handleIncrease = () => {
    const currentValue =
      typeof displayValue === "string"
        ? parseFloat(displayValue)
        : displayValue;
    const validCurrentValue = isNaN(currentValue)
      ? lastValidValue
      : currentValue;
    const newValue = Math.min(max, validCurrentValue + step);
    const roundedValue = Math.round(newValue * 100) / 100;

    setDisplayValue(formatMinOneDecimal(roundedValue));
    setLastValidValue(roundedValue);
    onChange?.(roundedValue);
  };

  return (
    <div className={`${css.container} ${disabled ? css.disabled : ""}`}>
      <button
        className={css.decrease}
        onMouseDown={(e) => {
          e.preventDefault();
          if (disabled) return;
          handleDecrease();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          if (disabled) return;
          handleDecrease();
        }}
        disabled={!!disabled}
        aria-disabled={disabled}
      >
        <Icon name="minus" width={12} height={12} color="#FAF4E6" />
      </button>
      <input
        ref={inputRef}
        type="text"
        inputmode="decimal"
        pattern="^[0-9]*[\.,]?[0-9]{0,2}$"
        value={displayValue}
        className={css.input}
        onInput={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
      />
      <button
        className={css.increase}
        onMouseDown={(e) => {
          e.preventDefault();
          if (disabled) return;
          handleIncrease();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          if (disabled) return;
          handleIncrease();
        }}
        disabled={!!disabled}
        aria-disabled={disabled}
      >
        <Icon name="plus" width={12} height={12} color="#FAF4E6" />
      </button>
    </div>
  );
};
